'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DEFAULT_V2_ENTITY_FILTER } from '@/_constants/entities';
import type {
  EntitiesColumnType,
  EntitiesV2FilterObject,
} from '@/_types/entities';
import {
  ENTITIES_COLUMNS_COOKIE,
  ENTITIES_PAGE_SIZE_COOKIE,
  ENTITIES_PAGE_COLUMNS_COOKIE,
} from '@/_constants/cookies';
import {
  setCookie,
  getCookie,
  deleteCookie,
  type CookieValueTypes,
} from 'cookies-next/client';
import type { EntityV2ProviderProps } from './EntityV2Provider.types';
import { Company } from '@/_types/models/company';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { CreateEntityDialog } from './_components/CreateEntityDialog';
import { CreateOwnerDialog } from './_components/CreateOwnerDialog';
import { EntityTagsDialog } from './_components/EntityTagsDialog';
import { areSameObjects } from '@/_utils/objects';
import { getAdminEntities } from '@/_api/v2/admin-entities';
import { SortingState } from '@tanstack/react-table';

const EntityV2Context = createContext<EntityV2ProviderProps>({
  allEntityIds: [],
  columns: [],
  editEntity: undefined,
  entitiesCount: 0,
  filter: DEFAULT_V2_ENTITY_FILTER,
  filteredEntities: [],
  isFiltering: false,
  isLoading: true,
  loadEntities: () => Promise.resolve(),
  onFilterChange: () => null,
  pagination: { pageIndex: 0, pageSize: 10 },
  reloadEntitiesCounts: () => Promise.resolve(),
  resetColumns: () => null,
  resetFilters: () => null,
  setColumns: () => null,
  setEditEntity: () => null,
  setFilter: () => null,
  setPagination: () => null,
  setSorting: () => null,
  sorting: [],
  toggleCreateEntityDialog: () => null,
  toggleCreateOwnerDialog: () => null,
  toggleEntityTagsDialog: () => null,
  updateSelectedEntity: (_company: Partial<Company>) => null,
});

const EntityV2Provider: any = ({ children }: any) => {
  const { isAdvisor, token } = useContext(UserContext);
  const { refreshCompanyDetails } = useContext(CompanyContext);

  const [editEntity, setEditEntity] = useState<Company | undefined>(undefined);
  const [showEntityTags, setShowEntityTags] = useState<boolean>(false);
  const [showCreateEntityDialog, setShowCreateEntityDialog] =
    useState<boolean>(false);
  const [showCreateOwnerDialog, setShowCreateOwnerDialog] =
    useState<boolean>(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filter, setFilter] = useState<EntitiesV2FilterObject>(
    DEFAULT_V2_ENTITY_FILTER,
  );
  const [columns, setColumns] = useState<Array<EntitiesColumnType>>([]);

  const toggleEntityTagsDialog = () => setShowEntityTags(!showEntityTags);
  const toggleCreateOwnerDialog = () =>
    setShowCreateOwnerDialog(!showCreateOwnerDialog);

  const onCloseOwnerDialog = () => {
    setShowCreateOwnerDialog(false);
    refreshCompanyDetails();
  };

  const resetColumns = () => {
    deleteCookie(ENTITIES_PAGE_COLUMNS_COOKIE);
    setColumns([]);
  };

  const toggleCreateEntityDialog = () =>
    setShowCreateEntityDialog(!showCreateEntityDialog);

  const isFiltering: boolean = useMemo(() => {
    const { firm_role_id: _0, ...cleanFilter } = filter;
    const { firm_role_id: _1, ...cleanDefaultFilter } =
      DEFAULT_V2_ENTITY_FILTER;

    return !areSameObjects(cleanFilter, cleanDefaultFilter);
  }, [filter]);

  const resetFilters = (): void => setFilter(DEFAULT_V2_ENTITY_FILTER);

  const onFilterChange = (
    newFilter: Partial<EntitiesV2FilterObject | object>,
  ) => {
    const newFilters: EntitiesV2FilterObject = {
      ...filter,
      ...newFilter,
    };

    return setFilter(newFilters);
  };

  const updateSelectedEntity = (company: Partial<Company>): void => {
    if (!company?.id) return;

    const existingCompanyIndex = filteredEntities.findIndex(
      (u) => u.id === company.id,
    );

    const newCompanies = [...filteredEntities];
    newCompanies[existingCompanyIndex] = {
      ...newCompanies[existingCompanyIndex],
      ...company,
    };
    setFilteredEntities(newCompanies);
  };

  useEffect(() => {
    const readCookies = () => {
      let columns: Array<EntitiesColumnType> = [];

      try {
        const entitiesColumnsCookie: CookieValueTypes = getCookie(
          ENTITIES_COLUMNS_COOKIE,
        );

        if (entitiesColumnsCookie) {
          const parsed: Array<EntitiesColumnType> = JSON.parse(
            entitiesColumnsCookie,
          );

          if (parsed && Array.isArray(parsed)) {
            columns = parsed;
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setColumns(columns);
      }
    };

    readCookies();
  }, []);

  useEffect(() => {
    // If we are searching, we want to start from the first page
    if (!!filter?.search_term) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [filter?.search_term]);

  useEffect(() => {
    const updateEntitiesCookie = () => {
      try {
        const entitiesColumnsCookie = JSON.stringify(columns);
        setCookie(ENTITIES_COLUMNS_COOKIE, entitiesColumnsCookie);
      } catch (error) {
        console.error(error);
      }
    };

    if (!!columns) updateEntitiesCookie();
  }, [columns]);

  const [filteredEntities, setFilteredEntities] = useState<Company[]>([]);
  const [entitiesCount, setEntitiesCount] = useState<number>(0);
  const [allEntityIds, setAllEntityIds] = useState<number[]>([]);

  // Load all IDs and count when filters change
  const loadAllIdsAndCount = useCallback(async () => {
    if (!token) return;
    if (!isAdvisor) return;

    try {
      const params = {
        token,
        ...DEFAULT_V2_ENTITY_FILTER,
        ...filter,
      };

      const { count } = await getAdminEntities({
        ...params,
        count: true,
      });

      setEntitiesCount(count);

      const { ids } = await getAdminEntities({
        ...params,
        only_ids: true,
      });

      setAllEntityIds(ids);
    } catch (error) {
      console.error(error);
      setEntitiesCount(0);
      setAllEntityIds([]);
    }
  }, [isAdvisor, filter, token]);

  // Load current page entities, triggers on pagination changes
  const loadPageEntities = useCallback(async () => {
    if (!token) return;
    if (!isAdvisor) return;

    try {
      setIsLoading(true);

      const params = {
        token,
        ...DEFAULT_V2_ENTITY_FILTER,
        ...filter,
        page: pagination?.pageIndex ?? 0,
        rows_per_page: pagination?.pageSize,
        sort_by: sorting?.[0]?.id || 'created_at',
        descending: !Boolean(sorting?.[0]?.desc),
      };

      const response = await getAdminEntities(params);
      setFilteredEntities(response);
    } catch (error) {
      console.error(error);
      setFilteredEntities([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    isAdvisor,
    filter,
    pagination?.pageIndex,
    pagination?.pageSize,
    sorting,
    token,
  ]);

  useEffect(() => {
    loadAllIdsAndCount();
  }, [loadAllIdsAndCount]);

  useEffect(() => {
    loadPageEntities();
  }, [loadPageEntities]);

  useEffect(() => {
    if (pagination?.pageSize !== filter.rows_per_page) {
      setCookie(ENTITIES_PAGE_SIZE_COOKIE, pagination?.pageSize.toString());
    }
  }, [pagination?.pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const entitiesPageSizeCookie = getCookie(ENTITIES_PAGE_SIZE_COOKIE);

    if (entitiesPageSizeCookie) {
      const parsed: number = parseInt(entitiesPageSizeCookie as string);

      if (parsed && !isNaN(parsed)) {
        setPagination((prev) => ({
          ...prev,
          pageSize: parsed,
        }));
      }
    }
  }, []);

  return (
    <EntityV2Context
      value={{
        allEntityIds,
        columns,
        editEntity,
        entitiesCount,
        filter,
        filteredEntities,
        isFiltering,
        isLoading,
        loadEntities: loadPageEntities,
        onFilterChange,
        pagination,
        reloadEntitiesCounts: loadAllIdsAndCount,
        resetColumns,
        resetFilters,
        setColumns,
        setEditEntity,
        setFilter,
        setPagination,
        setSorting,
        sorting,
        toggleCreateEntityDialog,
        toggleCreateOwnerDialog,
        toggleEntityTagsDialog,
        updateSelectedEntity,
      }}
    >
      <CreateEntityDialog
        isOpen={showCreateEntityDialog}
        onClose={toggleCreateEntityDialog}
      />
      <CreateOwnerDialog
        isOpen={showCreateOwnerDialog}
        onClose={onCloseOwnerDialog}
      />
      <EntityTagsDialog
        isOpen={showEntityTags}
        onClose={toggleEntityTagsDialog}
      />
      {children}
    </EntityV2Context>
  );
};

export { EntityV2Provider, EntityV2Context };
